/**
 * Payment State Helpers
 *
 * Pure utilities for payment-related state lookups.
 * For context update factories, see context-update.ts
 */

export type TreatmentStateName =
  | "defaultTreatment"
  | "specificTreatment"
  | "workersCompTreatment"
  | "epsTreatment";

/**
 * Determines the target state for BACK navigation based on previousTreatmentState.
 * Returns the XState target string for the treatment state.
 */
export function resolvePreviousTreatmentTarget(
  previousTreatmentState: string | undefined,
): string {
  switch (previousTreatmentState) {
    case "specificTreatment":
      return "#medrite-checkin.specificTreatment";
    case "defaultTreatment":
      return "#medrite-checkin.defaultTreatment";
    default:
      return "#medrite-checkin.storefrontBrowse";
  }
}

/**
 * Checks if the context has a specific previous treatment state.
 */
export function hasPreviousTreatmentState(
  context: { previousTreatmentState?: string },
  stateName: TreatmentStateName,
): boolean {
  return context.previousTreatmentState === stateName;
}

export type PaymentRoute =
  | "PAY_NOW"
  | "PAY_AT_DESK"
  | "PAY_LATER"
  | "COMPLETE"
  | null;

/**
 * Checks if the context has a specific payment route.
 */
export function hasPaymentRoute(
  context: { paymentRoute?: PaymentRoute },
  route: PaymentRoute,
): boolean {
  return context.paymentRoute === route;
}

/**
 * Shape of insurances in context
 */
export interface InsuranceData {
  primaryInsurance?: { insuranceId?: string; [key: string]: unknown };
  secondaryInsurance?: { insuranceId?: string; [key: string]: unknown };
}

/**
 * Extracts insurance IDs from context.
 * Only returns IDs when mode is INSURED.
 */
export function extractInsuranceIds(
  context: { insurances?: InsuranceData },
  mode: "CASH" | "INSURED" | string,
): string[] {
  if (mode !== "INSURED") {
    return [];
  }

  const insuranceIds: string[] = [];
  const insurances = context.insurances;

  if (insurances?.primaryInsurance?.insuranceId) {
    insuranceIds.push(insurances.primaryInsurance.insuranceId);
  }
  if (insurances?.secondaryInsurance?.insuranceId) {
    insuranceIds.push(insurances.secondaryInsurance.insuranceId);
  }

  return insuranceIds;
}
